import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Header, Grid, Image, Divider, Menu, Card, Label, Button, Icon } from 'semantic-ui-react';
import { EditComposer } from './EditComposer';

import { useInstruments } from '../../hooks/useInstruments';
import { useVoiceTypes } from '../../hooks/useVoiceTypes';
import { PageTitle } from '../common/PageTitle';
import { DateRange } from '../common/DateRange';
import { CardSong } from '../cards/CardSong';
import { CardSongCycle } from '../cards/CardSongCycle';
import { Moment } from 'react-moment';
import { EditSongVersion } from './EditSongVersion';
import { EditSongCycleVersion } from './EditSongCycleVersion';
import { useAuth0 } from '@auth0/auth0-react';
import RoleWrapper from "../common/RoleWrapper";
import { DeleteComposer } from './deletes/DeleteComposers';


export const ViewComposer = () => {
  const instrumentMap = useInstruments();
  const voiceTypeMap = useVoiceTypes();
  const [ data, setData ] = useState({profile: {}});
  const [ allSongs, setAllSongs ] = useState([]);
  const [ songCards, setSongCards ] = useState([]);
  const [ cycleCards, setCycleCards ] = useState([]);
  const [ spinnerActive, setSpinnerActive ] = useState(false);
  const [ displayError, setDisplayError ] = useState(false);
  const { id } = useParams();
  const { isAuthenticated, getIdTokenClaims} = useAuth0();
  const [roles, setRoles] = useState([]); // for role identification
  const namespace = "http://myapp.com"; // for the role selection

  useEffect(() => { // Authentication by role 
    const fetchRoles = async () => {
      if (isAuthenticated) {
        const claims = await getIdTokenClaims();
        setRoles(claims[namespace + "//roles"] || []);
      }
    };
    fetchRoles();
  }, [isAuthenticated, getIdTokenClaims]);

  useEffect(() => {
    async function getData() {
      const result = await fetch(`/go/api/v1/composers/search/${id}`);
      const json = await result.json();
      if (!!json.hits && !!json.hits.hits.length) {
        const src = json.hits.hits[0]._source;
        setData(src);
        //console.debug(src);
        await getSongs(src.id);
        await getSongCycles(src.id);
      }
    }

    async function getSongs(composerId) {
      const result = await fetch(`/go/api/v1/songs/search?q=composer_id:${composerId}`);
      const json = await result.json();
      //console.debug(json);
      setAllSongs(json.hits.hits.map(hit => hit._source));
      const cards = json.hits.hits.map((s, i) => {
        return (
          <CardSong data={s._source} key={i} id={s._id} />
        )
      });
      if (!!cards.length) {
        setSongCards(
          <React.Fragment>
            <Divider />
            <Header as='h3' color='green' className='subtitle'>
              Songs
              <Label size='mini' circular>{cards.length}</Label>
            </Header>
            <Card.Group itemsPerRow={3}>
              {cards}
            </Card.Group>
          </React.Fragment>
        );
      }
    }

    async function getSongCycles(composerId) {
      const result = await fetch(`/go/api/v1/song-cycles/search?q=composer_id:${composerId}`);
      const json = await result.json();
      const cards = json.hits.hits.map((s, i) => {
        return (
          <CardSongCycle data={s._source} key={i} id={s._id} />
        )
      });
      if (!!cards.length) {
        setCycleCards(
          <React.Fragment>
            <Divider />
            <Header as='h3' color='yellow' className='subtitle'>
              Song Cycles & Collections
              <Label size='mini' circular>{cards.length}</Label>
            </Header>
            <Card.Group itemsPerRow={3}>
              {cards}
            </Card.Group>
          </React.Fragment>
        );
      }
    }

    getData();
  }, []);

  const onComposerSubmitted = (composer, closeFn) => {
    setSpinnerActive(true);
    setDisplayError(false);
    const newProfile = {};
    Object.keys(composer.profile).map((key) => {
      newProfile[key.replace("_", "")] = composer.profile[key];
    });
    
    fetch(`/go/api/v1/profiles/${newProfile.id}`, {
      method: "PUT",
      body: JSON.stringify(newProfile),
      headers: {
        "Content-type": "application/json"
      }
    }).then((resp) => {
      if (resp.status === 200) {
        console.log("got good response", resp);
        closeFn();
        setData(composer);
      } else {
        console.log("got bad response", resp);
        setDisplayError(true);
      }
    }).catch((e) => {
      console.log("error", e);
      setDisplayError(true);
    }).finally(() => {
      setSpinnerActive(false);
    });
  };

  const onNewComposerSubmitted = (composer, closeFn) => {
    setSpinnerActive(true);
    setDisplayError(false);
    const newProfile = {};
    Object.keys(composer.profile).map((key) => {
      newProfile[key.replace("_", "")] = composer.profile[key];
    });
    newProfile.ID = 0;
    fetch(`/go/api/v1/profiles`, {
      method: "POST",
      body: JSON.stringify(newProfile),
      headers: {
        "Content-type": "application/json"
      }
    }).then((resp) => {
      if (resp.status === 200) {
        console.log("got good response for create profile");

        resp.json().then((respJson) => {
          console.log("got json", respJson);
          const newComposer = {};
          newComposer.ID = 0;
          newComposer.profileId = respJson.ID;
          fetch(`/go/api/v1/composers`, {
            method: "POST",
            body: JSON.stringify(newComposer),
            headers: {
              "Content-type": "application/json"
            }
          }).then((resp) => {
            if (resp.status === 200) {
              console.log("good response for create composer");

              resp.json().then((respJson) => {
                console.log("got second json", respJson);
                closeFn();
                window.location.href = `${respJson.ID}`;
              });
            } else {
              console.log("got bad response for create composer", resp);
              setDisplayError(true);
            }
          }).catch((e) => {
            console.log("error", e);
            setDisplayError(true);
          }).finally(() => {
            setSpinnerActive(false);
          });
        });
        
      } else {
        console.log("got bad response", resp);
        setDisplayError(true);
      }
    }).catch((e) => {
      console.log("error", e);
      setDisplayError(true);
      setSpinnerActive(false);
    });
  };


  const onDeleteComposer = (composer, closeFn) => {
    setSpinnerActive(true);
    setDisplayError(false);

    const composerId = composer.id;
    const profileId = composer.profile.id;

    fetch(`/go/api/v1/composers/${composerId}`, {
      method: "DELETE",
      headers: {
        "Content-type": "application/json"
      }
    }).then((resp) => {
	if (resp.status === 200 ){
	 console.log("got good response for delete composer");
	
	 fetch(`/go/api/v1/composers/search/${composerId}`,{
           method: "DELETE", 
	   headers: {
             "Content-type":"application/json"
           }
     }).then((resp) => {
       if (resp.status === 200) {
         console.log("got good response for delete search index of composer");

         fetch(`/go/api/v1/profiles/${profileId}`, {
           method: "DELETE",
           headers: {
             "Content-type": "application/json"
           }
         }).then((resp) => {
           if (resp.status === 200) {
            console.log("got good response for delete profile");

            closeFn();
            window.location.href = "/composers";
          } else {
            console.log("got bad response for delete profile", resp);
            setDisplayError(true);
          }
        }).catch((e) => {
          console.log("error deleting profile", e);
          setDisplayError(true);
        }).finally(() => {
          setSpinnerActive(false);
        });
      } else {
	console.log("got bad response deleting search of composer", resp);
	setDisplayError(true);
	setSpinnerActive(false);
      }
     }).catch((e) => {
	     console.log("error deleting search of composer",e);
	     setDisplayError(true);
	     setSpinnerActive(false);
      });
      } else {
        console.log("got bad response for delete composer", resp);
        setDisplayError(true);
        setSpinnerActive(false);
      }
    }).catch((e) => {
      console.log("error deleting composer", e);
      setDisplayError(true);
      setSpinnerActive(false);
    });
  };



  const editButton = (
    <Button basic icon labelPosition="left">
      <Icon name="add"/>
      Edit
    </Button>
  );

  const newButton = (
    <Button basic icon labelPosition="left">
      <Icon name="add"/>
      New Composer
    </Button>
  );

  const deleteButton = (
      <Button basic icon labelPosition="left">
        <Icon name="trash alternate"/>
        Delete Composer
      </Button>
  );

    const editModal = <EditComposer 
    openButton={editButton}
    onSuccess={onComposerSubmitted}
    initialData={data}
    spinnerActive={spinnerActive}
    displayError={displayError}
  />;
  const newModal = <EditComposer
    openButton={newButton}
    onSuccess={onNewComposerSubmitted}
    spinnerActive={spinnerActive}
    displayError={displayError}
  />;
  const deleteModal = <DeleteComposer
      openButton={deleteButton}
      onConfirm={onDeleteComposer}
      composer={data}
      spinnerActive={spinnerActive}
      displayError={displayError}
  />;

  const onNewSong = () => {};
  const onNewCycle = () => {};

  const newSongButton = (
    <Button basic icon labelPosition="left">
      <Icon name="add"/>
      New Song
    </Button>
  );

  const newCycleButton = (
    <Button basic icon labelPosition="left">
      <Icon name="add"/>
      New Song Cycle
    </Button>
  );

  const newSongModal = <EditSongVersion
    openButton={newSongButton}
    onSuccess={onNewSong}
    instrumentMap={instrumentMap}
    vtMap={voiceTypeMap}
    composerId={parseInt(id)}
  />;

  const newCycleModal = <EditSongCycleVersion
    songOptions={allSongs}
    openButton={newCycleButton}
    onSuccess={onNewCycle}
    composerId={parseInt(id)}
  />;

  //const modalButtons = isAuthenticated ? (
    //<span>
      //{editModal}
      //{newModal}
      //{newSongModal}
      //{newCycleModal}
    //</span>
  //) : null;

      const modalButtons = (
      <RoleWrapper roles={roles} requiredRole="Editor">
        {editModal} 
        {newModal} 
        {newSongModal}
        {newCycleModal}
        {deleteModal}
      </RoleWrapper>
    );

  const headertext = data.profile.non_personal ? "Text Source" : "Personal Profile"

  console.log("viewcomposer rendering data: ", data);

  return (
    <Container className='view-container'>
      <Grid>
        <Grid.Column width={3}>
          <Image fluid src={data.profile.image_url} />
          <Menu text vertical>
            <Menu.Item header>Navigation</Menu.Item>
            <Menu.Item link={true}>About</Menu.Item>
            <Menu.Item link={true}>Song Cycles &amp; Collections</Menu.Item>
            <Menu.Item link={true}>Songs</Menu.Item>
          </Menu>
        </Grid.Column>
        <Grid.Column width={13}>
          <div className='composer-header-bar'>
            <Header as='h3' color='blue' className='subtitle'>{headertext}</Header>
            <div className='composer-header-buttons'>
              {modalButtons}
            </div>
          </div>
          <Header as='h1' className='title' size='huge'>{data.profile.first_name} {data.profile.last_name}</Header>
          {data.profile.non_personal ? <></> : <><div className='composer-meta-bar'>
            <div><DateRange start={data.profile.birth_date} end={data.profile.death_date} /></div>
          </div></>}
          <Divider hidden />
          {/* <p>{data.profile.blurb}</p> */}
          {cycleCards}
          {songCards}
        </Grid.Column>
      </Grid>
    </Container>
  );
};
